import { Trans, useTranslation } from "react-i18next";
import { startCase } from "lodash";
import { X, Download, ArrowLeftRight, CreditCard } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { cardFallbackImage } from "./dashboard-wallet-widget";

const PRIMARY = "100,56,255";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  frontImage: string | null;
  backImage?: string | null;
}

export const WalletViewDialog = ({
  isOpen,
  onClose,
  title,
  frontImage,
  backImage,
}: IProps) => {
  const { t } = useTranslation("my-space");
  const [flipped, setFlipped] = useState(false);
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFlipped(false);
      requestAnimationFrame(() => {
        setVisible(true);
        setTimeout(() => setEntered(true), 80);
      });
    } else {
      setEntered(false);
      setVisible(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setEntered(false);
    setVisible(false);
    setTimeout(onClose, 280);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleDownload = async () => {
    const url = flipped ? backImage : frontImage;
    if (!url) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const ext = blob.type.split("/")[1] || "jpg";
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${title.replace(/\s+/g, "-").toLowerCase()}-${flipped ? "back" : "front"}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open((flipped ? backImage : frontImage) ?? "", "_blank");
    }
  };

  if (!isOpen) return null;

  const hasBack = Boolean(backImage);

  return createPortal(
    /* Backdrop */
    <div
      onClick={handleClose}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        background: "rgba(0,0,0,0.82)",
        backdropFilter: "blur(12px)",
        transition: "opacity 280ms ease",
        opacity: visible ? 1 : 0,
      }}
    >
      {/* Dot grid pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)`,
          backgroundSize: "28px 28px",
        }}
      />

      {/* Close */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        aria-label={t("ui.close")}
        className="absolute top-5 end-5 p-2.5 rounded-full transition-all duration-200"
        style={{
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.1)",
          color: "rgba(255,255,255,0.5)",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background =
            "rgba(255,255,255,0.14)";
          (e.currentTarget as HTMLElement).style.color = "white";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background =
            "rgba(255,255,255,0.07)";
          (e.currentTarget as HTMLElement).style.color =
            "rgba(255,255,255,0.5)";
        }}
      >
        <X className="w-4 h-4" />
      </button>

      {/* Main content */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col items-center w-full px-5"
        style={{
          maxWidth: 500,
          transform: entered
            ? "translateY(0) scale(1)"
            : "translateY(32px) scale(0.94)",
          opacity: entered ? 1 : 0,
          transition:
            "transform 420ms cubic-bezier(0.34,1.15,0.64,1), opacity 320ms ease",
        }}
      >
        {/* Header */}
        <div className="w-full flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div
                className="p-1.5 rounded-lg"
                style={{ background: `rgba(${PRIMARY},0.2)` }}
              >
                <CreditCard
                  className="w-3.5 h-3.5"
                  style={{ color: `rgba(${PRIMARY},1)` }}
                />
              </div>
              <span className="text-white/40 text-xs font-semibold uppercase tracking-widest">
                {t("ui.my_card_wallet")}
              </span>
            </div>
            <h2 className="text-white text-xl font-bold tracking-tight leading-tight">
              {startCase(title)}
            </h2>
            {hasBack && (
              <p
                className="text-xs font-semibold uppercase tracking-widest mt-1"
                style={{ color: "rgba(255,255,255,0.45)" }}
              >
                {flipped ? t("ui.back_side_2") : t("ui.front_side_2")}
              </p>
            )}
          </div>
        </div>

        {/* Card 3D scene */}
        <div className="w-full relative" style={{ perspective: "1400px" }}>
          {/* Bottom glow */}
          <div
            className="absolute bottom-[-8px] left-1/2 pointer-events-none"
            style={{
              transform: "translateX(-50%)",
              width: "60%",
              height: 36,
              borderRadius: "50%",
              background: `rgba(${PRIMARY},0.4)`,
              filter: "blur(24px)",
              transition: "opacity 400ms",
              opacity: entered ? 1 : 0,
            }}
          />

          {/* Card flipper — credit-card ratio 85.6:54 */}
          <div
            style={{
              position: "relative",
              width: "100%",
              paddingBottom: "63%",
              transformStyle: "preserve-3d",
              transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
              transition: "transform 560ms cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            {/* Front */}
            <div
              className="absolute inset-0 rounded-[22px] overflow-hidden"
              style={{
                backfaceVisibility: "hidden",
                boxShadow: `
                  0 40px 80px rgba(0,0,0,0.6),
                  0 0 0 1px rgba(255,255,255,0.1),
                  0 0 60px 8px rgba(${PRIMARY},0.18)
                `,
              }}
            >
              <img
                src={frontImage ?? cardFallbackImage}
                alt={t("ui.card_front_alt", { title: startCase(title) })}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = cardFallbackImage;
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 45%, rgba(0,0,0,0.05) 100%)",
                }}
              />
            </div>

            {/* Back */}
            {hasBack && (
              <div
                className="absolute inset-0 rounded-[22px] overflow-hidden"
                style={{
                  backfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                  boxShadow: `
                    0 40px 80px rgba(0,0,0,0.6),
                    0 0 0 1px rgba(255,255,255,0.1),
                    0 0 60px 8px rgba(${PRIMARY},0.18)
                  `,
                }}
              >
                <img
                  src={backImage ?? cardFallbackImage}
                  alt={t("ui.card_back_alt", { title: startCase(title) })}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      cardFallbackImage;
                  }}
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 45%, rgba(0,0,0,0.05) 100%)",
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Side indicator */}
        {hasBack && (
          <div className="flex items-center gap-2 mt-5">
            {[
              { key: "front", label: t("ui.front_side") },
              { key: "back", label: t("ui.back_side") },
            ].map((side, i) => {
              const isActive = (i === 0 && !flipped) || (i === 1 && flipped);
              return (
                <button
                  key={side.key}
                  onClick={() => setFlipped(i === 1)}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all duration-300"
                  style={
                    isActive
                      ? {
                          background: `rgba(${PRIMARY},0.22)`,
                          color: `rgb(${PRIMARY})`,
                          border: `1px solid rgba(${PRIMARY},0.4)`,
                        }
                      : {
                          background: "rgba(255,255,255,0.05)",
                          color: "rgba(255,255,255,0.35)",
                          border: "1px solid rgba(255,255,255,0.08)",
                        }
                  }
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: isActive
                        ? `rgb(${PRIMARY})`
                        : "rgba(255,255,255,0.2)",
                    }}
                  />
                  {side.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-3 mt-5 w-full">
          {hasBack && (
            <button
              onClick={() => setFlipped((f) => !f)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold transition-all duration-200"
              style={{
                background: `rgba(${PRIMARY},0.16)`,
                border: `1px solid rgba(${PRIMARY},0.3)`,
                color: "white",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  `rgba(${PRIMARY},0.28)`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  `rgba(${PRIMARY},0.16)`;
              }}
            >
              <ArrowLeftRight className="w-4 h-4" />
              {flipped ? t("ui.show_front") : t("ui.show_back")}
            </button>
          )}
          <button
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold transition-all duration-200"
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.7)",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(255,255,255,0.12)";
              (e.currentTarget as HTMLElement).style.color = "white";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background =
                "rgba(255,255,255,0.07)";
              (e.currentTarget as HTMLElement).style.color =
                "rgba(255,255,255,0.7)";
            }}
          >
            <Download className="w-4 h-4" />
            {t("ui.download")}
          </button>
        </div>

        {/* Esc hint */}
        <p className="text-white/18 text-[11px] mt-4 font-medium">
          <Trans
            i18nKey="my-space:ui.press_esc_or_click_outside_to_close"
            components={[
              <kbd
                key="0"
                className="font-mono bg-white/8 px-1.5 py-0.5 rounded text-white/25 text-[10px]"
              />,
            ]}
          />
        </p>
      </div>
    </div>,
    document.body,
  );
};
